/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useIssueLicense } from '@utils/api/back-office/licensing';
import { AdminLicenseRequest } from '@utils/types/back-office/licensing';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  request: AdminLicenseRequest | null;
}

interface FormData {
  org_limit: number;
  user_limit: number;
  max_processing: number;
  is_online: boolean;
  realtime: boolean;
  max_realtime_streams: number | null;
  realtime_languages: string; // csv
}

export default function IssueLicenseModal({ isOpen, onClose, request }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const issueLicense = useIssueLicense();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormData>({
    defaultValues: {
      org_limit: 1,
      user_limit: 10,
      max_processing: 3,
      is_online: true,
      realtime: false,
      max_realtime_streams: null,
      realtime_languages: ''
    }
  });

  const realtimeEnabled = watch('realtime');

  // Reset form when request changes
  useEffect(() => {
    if (request) {
      setValue('org_limit', request.org_limit);
      setValue('user_limit', request.user_limit);
      setValue('max_processing', request.max_processing);
      setValue('is_online', true);
      setValue('realtime', request.realtime);
      setValue('max_realtime_streams', request.max_realtime_streams);
      setValue(
        'realtime_languages',
        request.realtime_languages ? request.realtime_languages.join(',') : ''
      );
    }
  }, [request, setValue]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLicenseKey(null);
      setCopied(false);
    }
  }, [isOpen]);

  const onSubmit = async (data: FormData) => {
    if (!request) return;

    setIsSubmitting(true);
    try {
      const realtimeLanguages = data.realtime_languages
        ? data.realtime_languages
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        : null;

      const result = await issueLicense.mutateAsync({
        requestId: request.id,
        payload: {
          org_limit: data.org_limit,
          user_limit: data.user_limit,
          max_processing: data.max_processing,
          is_online: data.is_online,
          realtime: data.realtime,
          max_realtime_streams: data.realtime
            ? (data.max_realtime_streams ?? null)
            : null,
          realtime_languages: data.realtime ? realtimeLanguages : null
        }
      });

      // Show the license key
      setLicenseKey(result.license_key);
    } catch {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyKey = async () => {
    if (!licenseKey) return;

    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      toast.success(t('license-key-copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('failed-to-copy'));
    }
  };

  const handleClose = () => {
    reset();
    setLicenseKey(null);
    setCopied(false);
    onClose();
  };

  if (!request) return null;

  // Show license key result view
  if (licenseKey) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="medium"
        title={t('license-issued-successfully')}
        closeOnOutsideClick={false}
        closeOnEsc={false}
        footerActions={[
          <Button
            key="done"
            onClick={handleClose}
            label={t('done')}
            size="small"
            variant="primary"
          />
        ]}
      >
        <div className="space-y-4">
          {/* Warning */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-400/10 dark:bg-amber-400/15">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {t('license-key-warning')}
            </p>
          </div>

          {/* License Key Display */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('license-key')}
            </label>
            <div className="relative">
              <textarea
                readOnly
                value={licenseKey}
                rows={4}
                className="w-full p-3 pr-12 rounded-2xl border border-hairline bg-app text-xs font-mono resize-none"
                style={{ color: getColour('normalText') }}
              />
              <button
                type="button"
                onClick={handleCopyKey}
                className="absolute top-3 right-3 p-2 rounded-full border border-hairline bg-app-soft hover:opacity-80 transition-opacity"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy
                    className="h-4 w-4"
                    style={{ color: getColour('normalText') }}
                  />
                )}
              </button>
            </div>
          </div>

          {/* Details */}
          <div
            className="p-3 rounded-2xl border border-hairline bg-app text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            <p>
              <span style={{ color: getColour('normalText') }}>
                {t('licensee')}:
              </span>{' '}
              {request.target_org_name}
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  // Show form view
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="small"
      title={t('issue-license')}
      caption={t('issue-license-for-request', { id: request.id })}
      footerActions={[
        <Button
          key="cancel"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="issue"
          onClick={handleSubmit(onSubmit)}
          label={isSubmitting ? t('issuing') : t('issue-license')}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      <form className="space-y-4">
        {/* Request Info */}
        <div className="p-3 rounded-2xl border border-hairline bg-app text-sm">
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('target-org')}:
            </span>{' '}
            {request.target_org_name}
          </p>
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('reseller')}:
            </span>{' '}
            {request.reseller_org_name}
          </p>
        </div>

        {/* Limits - can override requested values */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            id="org_limit"
            label={t('org-limit')}
            type="number"
            min={1}
            size="small"
            {...register('org_limit', { valueAsNumber: true })}
          />
          <Input
            id="user_limit"
            label={t('user-limit')}
            type="number"
            min={1}
            size="small"
            {...register('user_limit', { valueAsNumber: true })}
          />
        </div>

        <Input
          id="max_processing"
          label={t('max-processing')}
          type="number"
          min={1}
          size="small"
          {...register('max_processing', { valueAsNumber: true })}
        />

        {/* Online Mode Toggle */}
        <div className="border-t border-hairline pt-4">
          <Checkbox
            id="is_online"
            label={t('online-mode')}
            {...register('is_online')}
            variant="primary"
            wrapperClass="flex items-center gap-2"
            labelClass="text-sm font-medium"
          />
          <p
            className="text-xs mt-1 ml-6"
            style={{ color: getColour('inactiveText') }}
          >
            {t('online-validation-description')}
          </p>
        </div>

        {/* Realtime entitlement */}
        <div className="border-t border-hairline pt-4 space-y-3">
          <Checkbox
            id="realtime"
            label={t('realtime-asr-add-on')}
            {...register('realtime')}
            variant="primary"
            wrapperClass="flex items-center gap-2"
            labelClass="text-sm font-medium"
          />
          <p
            className="text-xs ml-6 -mt-2"
            style={{ color: getColour('inactiveText') }}
          >
            {t('realtime-asr-help')}
          </p>

          {realtimeEnabled ? (
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="max_realtime_streams"
                label={t('max-realtime-streams')}
                type="number"
                min={1}
                size="small"
                {...register('max_realtime_streams', {
                  setValueAs: v =>
                    v === '' || v === null || v === undefined ? null : Number(v)
                })}
                placeholder="4"
              />
              <Input
                id="realtime_languages"
                label={t('realtime-languages')}
                size="small"
                {...register('realtime_languages')}
                placeholder="nl-nl, en-us"
              />
            </div>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
